import React, { useState, useEffect, useCallback, useRef } from "react";
import StatsCard from "./StatsCard";
import { ChevronDown, ChevronUp, Search, FilterX, X } from "lucide-react";
import UserTable from "./UserTable";
import CompanyTable from "./CompanyTable";
import AddUserModal from "../Modals/AddUserModal";
import { getAllUsers, getUsersStatsCardData, getCompanies, downloadUploadTemplate } from "../../requests.js";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import DataLoader from "../common/DataLoader.js";
import AddCompanyModal from "../Modals/AddCompanyModal.js";
import "../../_css/UserManagement.css";
import useDebounce from "../hooks/useDebounce.js";
import { useClickOutsideWithCallback } from "../hooks/useClickOutside.js";
import Pagination from "../common/Pagination";
import { notifyError, notifyInfo, notifySuccess } from "../../utils/helper.js";

const Management = () => {
    const [page, setPage] = useState(1);
    const [showDropdown, setDropdown] = useState(false);
    const [selectedTableType, setSelectedTableType] = useState("User");
    const [selectedPeriod, setSelectedPeriod] = useState("Year");

    // Default sort values
    const defaultSortField = "created_at";
    const defaultSortOrder = "desc";

    const [sortField, setSortField] = useState(defaultSortField);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({});
    const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const dispatch = useDispatch();
    const rowsPerPage = 10;

    const tableTypeRef = useRef(null);
    useClickOutsideWithCallback(tableTypeRef, () => setDropdown(false));

    const debouncedSearchQuery = useDebounce(() => searchQuery, 500);

    // [LOGIC] Check if any filter/search/sort is active
    const isFilterActive =
        searchQuery !== "" ||
        sortField !== defaultSortField ||
        sortOrder !== defaultSortOrder ||
        selectedPeriod !== "Year";

    // [LOGIC] Reset all filters - Managed ONLY here
    const handleClearFilters = useCallback(() => {
        setSearchQuery("");
        setSortField(defaultSortField);
        setSortOrder(defaultSortOrder);
        setSelectedPeriod("Year");
        setPage(1);
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const response = await getUsersStatsCardData();
            if (response.data?.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        dispatch(startLoader());
        try {
            const params = {
                page,
                rowsPerPage,
                selectedPeriod,
                search: debouncedSearchQuery,
                sortField,
                sortOrder
            };

            if (selectedTableType === "User") {
                const response = await getAllUsers(params);
                if (response?.data?.success) {
                    setUsers(response.data.users || []);
                    setTotalPages(response.data.totalPages || 0);
                    setTotalCount(response.data.totalCount || 0);
                }
            } else {
                const response = await getCompanies(params);
                if (response?.data?.success) {
                    setCompanies(response.data.companies || []);
                    setTotalPages(response.data.totalPages || 0);
                    setTotalCount(response.data.totalCount || 0);
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
            setIsSearching(false);
            dispatch(stopLoader());
        }
    }, [page, selectedTableType, selectedPeriod, debouncedSearchQuery, sortField, sortOrder, dispatch]);

    useEffect(() => {
        if (searchQuery !== debouncedSearchQuery) {
            setIsSearching(false);
        }
    }, [searchQuery, debouncedSearchQuery]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleDataRefresh = useCallback(() => {
        fetchData();
        fetchStats();
    }, [fetchData, fetchStats]);

    const handleTableTypeSelect = (type) => {
        setSelectedTableType(type);
        setDropdown(false);
        setPage(1);
        setSearchQuery("");
        // Optional: Reset filters when switching tabs if desired
        // handleClearFilters(); 
    };

    const handleSort = (field, order) => {
        setSortField(field);
        setSortOrder(order);
        setPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleTemplateDownload = async () => {
        notifyInfo("Your file is generating...");
        const type = selectedTableType === "User" ? "user" : "company";
        try {
            const res = await downloadUploadTemplate(type);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${type === "user" ? "User" : "Company"} Upload Template.csv`);
            document.body.appendChild(link);
            link.click();
            notifySuccess("Template downloaded successfully");
        } catch (error) {
            console.error(error);
            notifyError("Failed to download template");
        }
    };

    const handleCreated = useCallback(() => {
        setShowAddUserModal(false);
        setShowAddCompanyModal(false);
        setPage(1);
        fetchData();
        fetchStats();
    }, [fetchData, fetchStats]);

    const renderTable = () => {
        // PROPS REMOVED HERE as requested
        if (selectedTableType === "User") {
            return (
                <UserTable
                    filtereddata={users}
                    startIndex={(page - 1) * rowsPerPage}
                    onUserUpdate={handleDataRefresh}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            );
        } else {
            return (
                <CompanyTable
                    filtereddata={companies}
                    startIndex={(page - 1) * rowsPerPage}
                    onDataUpdate={handleDataRefresh}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            );
        }
    };

    return (
        <div className="management-container">
            <StatsCard Data={stats} />
            <div className="table-section">
                <div className="table-header">
                    <div className="controls-container d-flex align-items-center">
                        <div ref={tableTypeRef} style={{ position: 'relative', marginRight: '10px' }}>
                            <button onClick={() => setDropdown(!showDropdown)} className="dropdown-button">
                                {selectedTableType} {showDropdown ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />}
                            </button>

                            {showDropdown && (
                                <div className="table-dropdown-menu" style={{ width: "140px", display: 'block' }}>
                                    {["User", "Company"].map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="table-dropdown-item"
                                            onClick={() => handleTableTypeSelect(item)}
                                            style={{ color: selectedTableType === item ? "#23B4C3" : "" }}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Search Input */}
                        <div className="search-container">
                            <Search size={16} color="#6b7280" className="search-icon" />
                            <input
                                type="text"
                                placeholder={`Search ${selectedTableType.toLowerCase()}...`}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="search-input"
                            />
                        </div>

                        {/* [UI] Clear Filters Button - Managed Here */}
                        <button
                            className="btn d-flex align-items-center justify-content-center"
                            onClick={handleClearFilters}
                            disabled={!isFilterActive}
                            title="Clear all filters"
                            style={{
                                marginLeft: '10px',
                                height: '28px',
                                display: "flex",
                                gap: "3px",
                                alignContent: "center",
                                justifyContent: "space-between",
                                border: isFilterActive ? '1px solid #dc3545' : '1px solid #e5e7eb',
                                background: isFilterActive ? 'transparent' : '#f9fafb',
                                color: isFilterActive ? '#dc3545' : '#9ca3af',
                                borderRadius: '21px',
                                cursor: isFilterActive ? 'pointer' : 'default',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <span>Clear</span><X size={14} />
                        </button>
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                        <button onClick={handleTemplateDownload} className="add-button">
                            Download Upload Template
                        </button>
                        <button onClick={() => selectedTableType === "User" ? setShowAddUserModal(true) : setShowAddCompanyModal(true)} className="add-button">
                            Add {selectedTableType}
                        </button>
                    </div>

                </div>

                <div style={{ position: 'relative', minHeight: '300px' }}>
                    {(loading) && (
                        <div className="loader-overlay" style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(255,255,255,0.5)' }}>
                            <DataLoader />
                        </div>
                    )}
                    <div style={{ opacity: (loading || isSearching) ? 0.8 : 1 }}>
                        {renderTable()}
                    </div>
                </div>

                <Pagination
                    currentPage={page}
                    totalCount={totalCount}
                    pageSize={rowsPerPage}
                    onPageChange={(newPage) => setPage(newPage)}
                />
            </div>

            <AddUserModal show={showAddUserModal} onHide={() => setShowAddUserModal(false)} onUserCreated={handleCreated} />
            <AddCompanyModal show={showAddCompanyModal} onHide={() => setShowAddCompanyModal(false)} onCompanyCreated={handleCreated} />
        </div>
    );
};

export default Management;